import moment from "moment";
import React from "react";
import { useDispatch } from "react-redux";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { setActionLoading } from "../../../redux/reducers/loadingReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForClasses } from "../../../utils/confighelpers";
import { CoachField, cleanCoachName, rememberCoachName, useCoachNames } from "./coach";
import { LocationPicker, locationPayload } from "./locations";
import { Card, FcDrawer, FieldError, FieldLabel, Icon, inputClass, MiniCalendar, ymd } from "./ui";

/**
 * "Add sessions" drawer for My Schedule (new design).
 * The page works out the exact session dates and sends them as plain "YYYY-MM-DD" dates
 * (recurrenceType "custom") to schools/scheduleTheClass: first a preview call (isPublished:false),
 * then the real one (isPublished:true), as the old flow did.
 * Coach: pre-filled with the class's default coach; another name applies only to the sessions created here
 * (sent as instructorName; empty = the class's default).
 */

const MODES = [
  ["once", "Once"],
  ["weekly", "Weekly"],
  ["daily", "Daily"],
  ["custom", "Custom dates"],
];
const WEEKDAYS = [
  [1, "Mo", "Monday"],
  [2, "Tu", "Tuesday"],
  [3, "We", "Wednesday"],
  [4, "Th", "Thursday"],
  [5, "Fr", "Friday"],
  [6, "Sa", "Saturday"],
  [0, "Su", "Sunday"],
];
const MAX_SESSIONS = 366;

const emptyForm = () => ({
  mode: "weekly",
  startDate: ymd(new Date()),
  endDate: ymd(moment().add(8, "weeks").toDate()),
  time: "",
  weekdays: [],
  customDates: [],
  location: null,
});

export function buildSessionDates(form) {
  let dates = [];
  if (form.mode === "custom") {
    dates = [...(form.customDates || [])];
  } else if (form.startDate) {
    const start = moment(form.startDate, "YYYY-MM-DD");
    const end = form.mode === "once" ? start.clone() : moment(form.endDate || form.startDate, "YYYY-MM-DD");
    for (let d = start.clone(), n = 0; d.isSameOrBefore(end, "day") && n < 800; d.add(1, "day"), n++) {
      if (form.mode === "weekly" && (form.weekdays || []).indexOf(d.day()) === -1) continue;
      dates.push(d.format("YYYY-MM-DD"));
    }
  }
  dates = Array.from(new Set(dates)).sort();
  if (!form.time) return { all: dates, dates };
  const now = moment();
  return { all: dates, dates: dates.filter((x) => moment(`${x} ${form.time}`, "YYYY-MM-DD HH:mm").isAfter(now)) };
}

const fmtDay = (x) => moment(x, "YYYY-MM-DD").format("ddd D MMM");

export default function AddSessionsDrawer({ open, onClose, classList = [], initialClassId = "", onAdded = () => null }) {
  const { addSuccessToast, addErrorToast } = useToast();
  const dispatch = useDispatch();
  const [classId, setClassId] = React.useState("");
  const [form, setForm] = React.useState(emptyForm);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [coach, setCoach] = React.useState("");
  const coachNames = useCoachNames(open);

  React.useEffect(() => {
    if (open) {
      setClassId(initialClassId || "");
      setForm(emptyForm());
      setErrors({});
      setSaving(false);
    }
  }, [open, initialClassId]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k] || errors.summary) setErrors((e) => ({ ...e, [k]: "", summary: "" }));
  };

  const selected = classList.find((c) => String(c.id) === String(classId));
  const cls = selected?.data || {};
  const defaultCoach = cleanCoachName(cls.instructorName);

  // Picking (another) class fills in that class's default coach.
  React.useEffect(() => {
    setCoach(defaultCoach);
  }, [classId, defaultCoach]);
  const duration = Number(cls.duration) || 0;
  const endTime = form.time && duration ? moment(form.time, "HH:mm").add(duration, "minutes").format("HH:mm") : "";
  const { all, dates } = buildSessionDates(form);
  const today = ymd(new Date());

  const validate = () => {
    const e = {};
    if (!selected) e.classId = "Pick the class you want to schedule.";
    if (form.mode !== "custom") {
      if (!form.startDate) e.startDate = "Pick a date.";
      else if (form.startDate < today) e.startDate = "This date is in the past.";
      if (form.mode !== "once") {
        if (!form.endDate) e.endDate = "Pick an end date.";
        else if (form.startDate && form.endDate < form.startDate) e.endDate = "Has to be on or after the start date.";
      }
    }
    if (form.mode === "weekly" && !form.weekdays.length) e.weekdays = "Pick at least one day.";
    if (form.mode === "custom" && !form.customDates.length) e.customDates = "Pick at least one date.";
    if (!form.time) e.time = "Pick a start time.";
    if (!form.location) e.location = "Pick where the class takes place.";
    if (!Object.keys(e).length) {
      if (!dates.length) e.summary = all.length ? "All of these dates and times are already in the past." : "No sessions fall in this period. Check the dates and days.";
      else if (dates.length > MAX_SESSIONS) e.summary = `That's ${dates.length} sessions. Please choose a shorter period (max ${MAX_SESSIONS}).`;
    }
    return e;
  };

  const callSchedule = async (body) => {
    const res = await axiosInstance({ method: "post", url: `${getUrlForClasses()}scheduleTheClass`, data: body });
    return res?.data || {};
  };

  const handleSubmit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    const t = moment(form.time, "HH:mm");
    const body = {
      isPublished: false,
      classId: selected.id,
      className: cls.className || selected.label,
      startDate: dates[0],
      timing: t.format("hh:mm a"),
      endAfter: "",
      meridiem: t.format("A"),
      ...(form.mode !== "once" ? { recurrenceType: "custom" } : {}),
      endBy: dates[dates.length - 1],
      utcOffset: new Date().getTimezoneOffset(),
      daysInWeek: dates,
      classStartDateTime: moment(`${dates[0]} ${form.time}`, "YYYY-MM-DD HH:mm").toISOString(),
      location: locationPayload(form.location),
      instructorName: cleanCoachName(coach),
    };
    setSaving(true);
    dispatch(setActionLoading(true));
    try {
      const preview = await callSchedule(body); // server-side checks (overlaps, quota), creates nothing
      if (preview.statusCode !== 200) {
        setErrors({ summary: typeof preview.message === "string" ? preview.message : "Couldn't add these sessions." });
        return;
      }
      const result = await callSchedule({ ...body, isPublished: true });
      if (result.statusCode !== 200) {
        setErrors({ summary: typeof result.message === "string" ? result.message : "Couldn't add these sessions." });
        return;
      }
      const count = Array.isArray(result.data) ? result.data.length : dates.length;
      rememberCoachName(body.instructorName);
      addSuccessToast(`${count} ${count === 1 ? "session" : "sessions"} added to ${body.className}`);
      onAdded(count, selected);
      onClose();
    } catch (err) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      dispatch(setActionLoading(false));
      setSaving(false);
    }
  };

  const count = dates.length;
  const primaryLabel = count > 0 ? `Add ${count} ${count === 1 ? "session" : "sessions"}` : "Add sessions";

  return (
    <FcDrawer
      open={open}
      onClose={onClose}
      eyebrow="ADD SESSIONS"
      title="Put a class on your timetable"
      subtitle="Pick the class, when it runs and how often."
      primaryLabel={primaryLabel}
      onPrimary={handleSubmit}
      busy={saving}
    >
      <Card>
        <FieldLabel htmlFor="fc-sess-class">Class</FieldLabel>
        <div className="relative">
          <select
            id="fc-sess-class"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              if (errors.classId) setErrors((x) => ({ ...x, classId: "" }));
            }}
            className={classNames(inputClass(errors.classId), "appearance-none pr-11", classId ? "" : "text-gray-400")}
          >
            <option value="" disabled>Select a class</option>
            {classList.map((c) => (
              <option key={c.id} value={c.id} className="text-gray-900">{c.data?.className || c.label}</option>
            ))}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-500"><Icon className="h-5 w-5" d={<path d="m6 9 6 6 6-6" />} /></span>
        </div>
        <FieldError>{errors.classId}</FieldError>
        {selected && (
          <div className="mt-3 flex items-center gap-3 rounded-2xl bg-[#F7F4F8] p-3">
            {cls.classPhoto ? <img src={cls.classPhoto} alt="" className="h-11 w-14 shrink-0 rounded-xl object-cover" /> : <span className="h-11 w-14 shrink-0 rounded-xl bg-[#F3E9FF]" />}
            <span className="min-w-0 text-sm text-gray-700">
              <span className="block truncate font-extrabold text-gray-900">{cls.className}</span>
              <span className="block truncate text-xs">{[cls.classType, duration ? `${duration} min` : ""].filter(Boolean).join(" · ")}</span>
            </span>
          </div>
        )}
        {selected && (
          <div className="mt-4">
            <CoachField
              id="fc-sess-coach"
              label="Coach"
              value={coach}
              onChange={setCoach}
              names={coachNames}
              hint={
                defaultCoach && !cleanCoachName(coach)
                  ? `Left empty, these sessions get the class's default coach (${defaultCoach}).`
                  : defaultCoach && cleanCoachName(coach) !== defaultCoach
                  ? `Only these sessions. The class's default coach stays ${defaultCoach}.`
                  : defaultCoach
                  ? "The class's default coach. Change it for these sessions if someone else teaches them."
                  : "Optional. Who teaches these sessions?"
              }
            />
          </div>
        )}
      </Card>

      <Card title="When">
        <div role="tablist" aria-label="How often" className="grid grid-cols-4 gap-1 rounded-2xl bg-[#F3EEF5] p-1">
          {MODES.map(([k, l]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={form.mode === k}
              onClick={() => {
                setField("mode", k);
                setErrors({});
              }}
              className={classNames("h-10 rounded-xl text-sm font-extrabold", form.mode === k ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-5">
          {form.mode === "weekly" && (
            <div>
              <FieldLabel>On these days</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(([d, short, full]) => {
                  const on = form.weekdays.indexOf(d) > -1;
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-label={full}
                      aria-pressed={on}
                      onClick={() => setField("weekdays", on ? form.weekdays.filter((x) => x !== d) : [...form.weekdays, d])}
                      className={classNames("h-11 w-12 rounded-2xl text-sm font-extrabold transition-colors", on ? "bg-brown-coffee text-white" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50")}
                    >
                      {short}
                    </button>
                  );
                })}
              </div>
              <FieldError>{errors.weekdays}</FieldError>
            </div>
          )}

          {form.mode === "custom" ? (
            <div>
              <FieldLabel>Pick the dates</FieldLabel>
              <MiniCalendar mode="multi" value={form.customDates} minDate={today} onChange={(v) => setField("customDates", v)} />
              <FieldError>{errors.customDates}</FieldError>
            </div>
          ) : (
            <div className={classNames("grid gap-4", form.mode === "once" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2")}>
              <div>
                <FieldLabel htmlFor="fc-sess-start">{form.mode === "once" ? "Date" : "Starts"}</FieldLabel>
                <input id="fc-sess-start" type="date" min={today} value={form.startDate} onChange={(e) => setField("startDate", e.target.value)} className={inputClass(errors.startDate)} />
                <FieldError>{errors.startDate}</FieldError>
              </div>
              {form.mode !== "once" && (
                <div>
                  <FieldLabel htmlFor="fc-sess-end">Ends</FieldLabel>
                  <input id="fc-sess-end" type="date" min={form.startDate || today} value={form.endDate} onChange={(e) => setField("endDate", e.target.value)} className={inputClass(errors.endDate)} />
                  <FieldError>{errors.endDate}</FieldError>
                </div>
              )}
            </div>
          )}

          <div className="sm:max-w-[calc(50%-8px)]">
            <FieldLabel htmlFor="fc-sess-time">Start time</FieldLabel>
            <input id="fc-sess-time" type="time" step="300" value={form.time} onChange={(e) => setField("time", e.target.value)} className={inputClass(errors.time)} />
            {endTime && <p className="mt-1.5 text-xs text-gray-500">Ends at {endTime} ({duration} min)</p>}
            <FieldError>{errors.time}</FieldError>
          </div>
        </div>
      </Card>

      <Card title="Where">
        <LocationPicker value={form.location?._id} onChange={(id, loc) => setField("location", loc)} error={errors.location} />
      </Card>

      <Card>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-lg font-black text-gray-900">{count} {count === 1 ? "session" : "sessions"}</span>
          {count > 0 && <span className="text-sm text-gray-500">{fmtDay(dates[0])}{count > 1 ? ` – ${fmtDay(dates[dates.length - 1])}` : ""}</span>}
        </div>
        {count > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {dates.slice(0, 12).map((x) => (
              <span key={x} className="rounded-full bg-[#F3EEF5] px-2.5 py-1 text-xs font-bold text-gray-700">{fmtDay(x)}</span>
            ))}
            {count > 12 && <span className="px-1 py-1 text-xs font-bold text-gray-500">+{count - 12} more</span>}
          </div>
        )}
        {all.length > count && count > 0 && <p className="mt-2 text-xs text-gray-500">{all.length - count} date(s) already passed and are skipped.</p>}
        {count === 0 && !errors.summary && <p className="mt-1 text-sm text-gray-500">Pick the days and a time to see the sessions here.</p>}
        {errors.summary && (
          <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
            {errors.summary}
          </div>
        )}
      </Card>
    </FcDrawer>
  );
}
